import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import ContentLayout from "../components/ContentLayout";
import Button from "../components/Button";
import Icon from "@material-ui/core/Icon";
import { RouteComponentProps } from "react-router-dom";
import { SELF_SERVICE_REPORTS_LIST } from "constants/routes";
import FieldMultiSelect from "../components/FieldMultiSelect";
import FieldGroup from "../interfaces/FieldGroup";
import FieldSelectionMap from "../interfaces/FieldSelectionMap";
import ReportNameInput from "../components/ReportNameInput";
import { Switch } from "@material-ui/core";
import SelfServiceReport from "../interfaces/SelfServiceReport";
import {
  createConfigsFromFieldMaps,
  createFieldConfigMap,
  createFieldMap,
} from "./utils";
import { getReportPreviewData } from "./service";
import LoadingScreen from "../components/LoadingScreen";
import ReportPreviewData from "./interfaces/ReportPreviewData";
import useDataLoader from "hooks/useDataLoader";
import {
  getSelfServiceReportDataPreview,
  saveSelfServiceReport,
} from "../service";
import UnstyledGrid from "../components/Grid";
import {
  GridColumn,
  GridColumnReorderEvent,
  GridColumnResizeEvent,
  GridDataStateChangeEvent,
} from "@progress/kendo-react-grid";
import { DataResult, process, State } from "@progress/kendo-data-query";
import HeaderCell from "../components/Grid/HeaderCell";
import ColumnMenu from "../components/Grid/ColumnMenu";
import ColumnInfo from "../interfaces/ColumnInfo";
import FieldConfigMap from "../interfaces/FieldConfigMap";
import GridLoadingOverlay from "./GridLoadingOverlay";
import EmptyGridOverlay from "./EmptyGridOverlay";
import { debounce } from "lodash";
import DataSourceItem from "../interfaces/DataSourceItem";

const Main = styled.div`
  padding: 30px 40px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const Toolbar = styled.div`
  display: flex;
  margin-bottom: 30px;
`;

const FixedWidthButton = styled(Button)`
  width: 100px;
`;

const SwitchLabel = styled.label`
  display: inline-block;
  user-select: none;
  color: #424242;
  cursor: pointer;
  margin: 0 auto 0 20px;
  font-family: Inter, system-ui;
  font-size: 16px;
  font-weight: 400;
`;

const GridWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  height: 0;
`;

const Grid = styled(UnstyledGrid)`
  height: 100%;
`;

const highlightReportName = (element: HTMLInputElement | null) => {
  if (element) {
    element.focus();
    element.classList.add("highlighted");
    setTimeout(() => {
      element.classList.remove("highlighted");
    }, 800);
  }
};

const defaultUuid = "00000000-0000-0000-0000-000000000000";

const emptyReport: SelfServiceReport = {
  selfServiceReportId: defaultUuid, // needed for the validation
  userId: defaultUuid, // needed for the validation, the value will be overridden on a server
  name: "",
  publicToTenant: false,
  fieldConfigs: [],
  tableSettings: {},
};

const initState = { take: 100, skip: 0 };

interface ManageReportProps extends RouteComponentProps {}

const ManageReport = ({ history, match }: ManageReportProps) => {
  // @ts-ignore
  const reportId = match.params.selfServiceReportId;

  const [fieldGroups, setFieldGroups] = useState<FieldGroup[]>([]);
  const [report, setReport] = useState<SelfServiceReport>(emptyReport);

  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const [dataSource, setDataSource] = useState<DataSourceItem[]>([]);
  const [dataState, setDataState] = useState<State>(initState);
  const [result, setResult] = useState<DataResult>(process([], dataState));

  const [fieldSelectionMap, setFieldSelectionMap] = useState<FieldSelectionMap>(
    {}
  );
  const fieldConfigMap = useRef<FieldConfigMap>({});

  const getPreviewDataFn = () => getReportPreviewData(reportId);
  const { isLoading, data: previewData } =
    useDataLoader<ReportPreviewData>(getPreviewDataFn);

  useEffect(() => {
    if (!previewData) {
      return;
    }

    setFieldGroups(previewData.nonDefaultFieldGroups);

    const loadedReport = previewData.report;

    if (loadedReport) {
      setReport(loadedReport);

      const selectedFieldIds = loadedReport.fieldConfigs.map((c) => c.fieldId);
      setFieldSelectionMap(
        createFieldMap(previewData.nonDefaultFieldGroups, selectedFieldIds)
      );

      fieldConfigMap.current = createFieldConfigMap(
        previewData.allFields,
        loadedReport.fieldConfigs
      );

      const loadedColumns = previewData.reportData?.columns ?? [];
      setColumns(loadedColumns);

      const loadedDataSource = previewData.reportData?.dataSource ?? [];
      setDataSource(loadedDataSource);

      const loadedState = loadedReport?.tableSettings.dataState;
      const newState = { ...initState, ...loadedState };
      setDataState(newState);

      const newResult = process(loadedDataSource.slice(0), newState);
      setResult(newResult);
    } else {
      setFieldSelectionMap(createFieldMap(previewData.nonDefaultFieldGroups));
    }
  }, [previewData]);

  const onDataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
    setResult(process(dataSource.slice(0), event.dataState));
  };

  const onColumnResize = (event: GridColumnResizeEvent) => {
    if (!event.end) {
      // the user hasn't dropped the resize handler
      return;
    }

    const column = event.columns.find((col) => col.id === event.targetColumnId);

    const field = column?.field ?? "";
    const fieldConfig = fieldConfigMap.current[field];

    if (fieldConfig) {
      fieldConfig.width = event.newWidth;
    } else {
      fieldConfigMap.current[field] = { width: event.newWidth };
    }

    setColumns(event.columns as ColumnInfo[]);
  };

  const onColumnReorder = (event: GridColumnReorderEvent) => {
    event.columns.forEach((column) => {
      const field = column.field ?? "";
      const fieldConfig = fieldConfigMap.current[field];

      if (fieldConfig) {
        fieldConfig.orderIndex = column.orderIndex;
      } else {
        fieldConfigMap.current[field] = {
          orderIndex: column.orderIndex,
          width: null,
        };
      }
    });

    setColumns(event.columns as ColumnInfo[]);
  };

  const areAnyFieldsSelected = useMemo(
    () => Object.keys(fieldSelectionMap).some((key) => fieldSelectionMap[key]),
    [fieldSelectionMap]
  );

  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>();

  const loadPreview = useMemo(
    () =>
      debounce(
        async (
          data: ReportPreviewData,
          selectionMap: FieldSelectionMap,
          configMap: FieldConfigMap,
          state: State
        ) => {
          if (data) {
            const fieldConfigs = createConfigsFromFieldMaps(
              data.defaultFieldGroups,
              data.nonDefaultFieldGroups,
              selectionMap,
              configMap
            );
            const loadedData = await getSelfServiceReportDataPreview(
              fieldConfigs
            );
            setColumns(loadedData.columns);
            setDataSource(loadedData.dataSource);
            setResult(process(loadedData.dataSource.slice(0), state));
            setIsPreviewLoading(false);
          }
        },
        250
      ),
    []
  );

  const firstPreviewLoad = useRef<boolean>(true);

  useEffect(() => {
    if (previewData) {
      if (areAnyFieldsSelected && !firstPreviewLoad.current) {
        setIsPreviewLoading(true);

        loadPreview(
          previewData,
          fieldSelectionMap,
          fieldConfigMap.current,
          dataState
        );
      }

      firstPreviewLoad.current = false;
    }
  }, [fieldSelectionMap]);

  const reportNameRef = useRef<HTMLInputElement | null>(null);

  const saveReport = async () => {
    if (!report.name) {
      highlightReportName(reportNameRef.current);
      return;
    }
    if (!previewData) {
      return;
    }

    const fieldConfigs = createConfigsFromFieldMaps(
      previewData.defaultFieldGroups,
      previewData.nonDefaultFieldGroups,
      fieldSelectionMap,
      fieldConfigMap.current
    );

    const newReport = { ...report, fieldConfigs, tableSettings: { dataState } };
    const newReportId = await saveSelfServiceReport(newReport);
    history.push(`/reporting/self-service-reports/view/${newReportId}`);
  };

  return (
    <ContentLayout
      leftColumn={
        <Button variant="text" color="primary" onClick={() => history.goBack()}>
          <Icon>arrow_back</Icon>Back
        </Button>
      }
      centerColumn={
        <ReportNameInput
          ref={reportNameRef}
          name={report.name}
          setName={(name) => setReport({ ...report, name })}
          placeholder="Enter template name"
        />
      }
    >
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <Main>
          <Toolbar>
            <FieldMultiSelect
              fieldGroups={fieldGroups}
              fieldSelectionMap={fieldSelectionMap}
              setFieldSelectionMap={setFieldSelectionMap}
            />
            <SwitchLabel>
              <Switch
                style={{ height: "38px" }}
                checked={report.publicToTenant}
                onChange={() =>
                  setReport({
                    ...report,
                    publicToTenant: !report.publicToTenant,
                  })
                }
                color="primary"
              />
              Share with my company
            </SwitchLabel>
            <FixedWidthButton
              variant="outlined"
              color="primary"
              style={{ marginLeft: "20px" }}
              onClick={() => history.push(SELF_SERVICE_REPORTS_LIST)}
            >
              Cancel
            </FixedWidthButton>
            <FixedWidthButton
              variant="contained"
              color="primary"
              style={{ marginLeft: "20px" }}
              onClick={saveReport}
              disabled={!areAnyFieldsSelected}
            >
              Save
            </FixedWidthButton>
          </Toolbar>
          <GridWrapper>
            <Grid
              data={result}
              {...dataState}
              onDataStateChange={onDataStateChange}
              sortable
              pageable
              resizable
              onColumnResize={onColumnResize}
              reorderable
              onColumnReorder={onColumnReorder}
            >
              {columns.map((col, index) => (
                <GridColumn
                  key={index}
                  {...col}
                  columnMenu={ColumnMenu}
                  headerCell={HeaderCell}
                />
              ))}
            </Grid>
            {isPreviewLoading && <GridLoadingOverlay />}
            {!areAnyFieldsSelected && <EmptyGridOverlay />}
          </GridWrapper>
        </Main>
      )}
    </ContentLayout>
  );
};

export default ManageReport;
